import { get } from '@/services/index.js';

/* Module-level, not component state: BeDigitalMap unmounts on every navigation
   away from the map and MapViewport is force-remounted on every map swap, so a
   cache living inside either of them would be thrown away exactly when it is
   most useful. Nothing here is reactive -- callers copy what they need into
   their own data(). */
const mapDetailCache = {};
const roomTypeCache = {};
const imageCache = {};
/* Resolved values, kept alongside the promise caches above so a component that
   remounts (e.g. switching away from the map tab and back) can read data it
   already has synchronously on creation, instead of waiting a tick on a
   Promise it knows will resolve from cache anyway -- that tick is what used to
   show a "reloading" flash even though no request was actually re-sent. */
const mapDetailResolved = {};
const roomTypeResolved = {};

/**
 * Map detail (image + areas) for one booking map.
 * @param {string} mapId
 * @returns {Promise<Object|null>} null when the API reports a failure
 */
export function fetchMapDetail(mapId) {
  // the promise itself is cached, so parallel callers share one request
  if (mapDetailCache[mapId]) return mapDetailCache[mapId];

  const request = get('CORE_HOTEL', 'merchant-booking-map/' + mapId)
    .then((response) => {
      const res = response.data;
      if (!res.success) return null;
      mapDetailResolved[mapId] = res.data;
      return res.data;
    })
    .catch(() => {
      // a failed lookup must not be remembered -- the next visit should retry
      delete mapDetailCache[mapId];
      return null;
    });

  mapDetailCache[mapId] = request;
  return request;
}

/**
 * Synchronous read of a map detail already fetched this session.
 * @param {string} mapId
 * @returns {Object|undefined} undefined when not yet resolved
 */
export function getCachedMapDetail(mapId) {
  return mapDetailResolved[mapId];
}

/**
 * Room type detail, the source of truth for map pins. Independent of the search
 * filter, so one fetch per room type is enough for the whole session.
 * @param {string} merchantId
 * @param {string} roomTypeId
 * @returns {Promise<Object|null>} null when the room type no longer exists
 */
export function fetchRoomTypeDetail(merchantId, roomTypeId) {
  const key = merchantId + ':' + roomTypeId;
  if (roomTypeCache[key]) return roomTypeCache[key];

  const request = get('CORE_HOTEL', 'room-type/' + roomTypeId, {
    merchant_id: merchantId,
  })
    .then((response) => {
      const res = response.data;
      const detail = res.success ? res.data : null;
      roomTypeResolved[key] = detail;
      return detail;
    })
    .catch(() => {
      roomTypeResolved[key] = null;
      return null;
    });

  /* A miss IS cached here, unlike map detail: a deleted room type would
     otherwise be re-requested on every single map render. */
  roomTypeCache[key] = request;
  return request;
}

/**
 * Synchronous read of a room type detail already fetched this session. A
 * cached miss (room type no longer exists) resolves to null, distinguished
 * from "not fetched yet" via hasCachedRoomTypeDetail.
 * @param {string} merchantId
 * @param {string} roomTypeId
 * @returns {Object|null|undefined}
 */
export function getCachedRoomTypeDetail(merchantId, roomTypeId) {
  return roomTypeResolved[merchantId + ':' + roomTypeId];
}

export function hasCachedRoomTypeDetail(merchantId, roomTypeId) {
  const key = merchantId + ':' + roomTypeId;
  return Object.prototype.hasOwnProperty.call(roomTypeResolved, key);
}

/**
 * Resolves once the browser holds the decoded bitmap, so the <img> that mounts
 * afterwards paints in the same frame instead of flashing empty.
 * @param {string} url
 * @returns {Promise<void>} always resolves -- a broken image must not stall the map
 */
export function preloadImage(url) {
  if (!url) return Promise.resolve();
  if (imageCache[url]) return imageCache[url].promise;

  const image = new Image();
  const promise = new Promise((resolve) => {
    image.onload = resolve;
    image.onerror = resolve;
  });
  image.src = url;

  // the Image object is kept referenced on purpose: dropping it lets the
  // browser evict the decoded bitmap and the next swap flashes again
  imageCache[url] = { image: image, promise: promise };
  return promise;
}

export function clearMapDataCache() {
  Object.keys(mapDetailCache).forEach((key) => delete mapDetailCache[key]);
  Object.keys(mapDetailResolved).forEach((key) => delete mapDetailResolved[key]);
  Object.keys(roomTypeCache).forEach((key) => delete roomTypeCache[key]);
  Object.keys(roomTypeResolved).forEach((key) => delete roomTypeResolved[key]);
  Object.keys(imageCache).forEach((key) => delete imageCache[key]);
}



// WEBPACK FOOTER //
// ./src/components/hotel/be/v3/digital-map/mapDataCache.js